import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from 'context/SessionContext';
import { buildSessionExpiredPath } from 'utils/pathBuilder';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
] as const;

/**
 * Monitors user activity and logs out the session after 30 minutes of inactivity.
 * Only active when a session exists.
 */
export const useIdleTimeout = (): void => {
  const { session, clearSession } = useSession();
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTimeout = useCallback(() => {
    clearSession();
    navigate(buildSessionExpiredPath(), { replace: true });
  }, [clearSession, navigate]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(handleTimeout, IDLE_TIMEOUT_MS);
  }, [handleTimeout]);

  useEffect(() => {
    if (!session) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return () => {};
    }

    resetTimer();

    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [session, resetTimer]);
};
